/**
 * <s-thumb-gallery> — main image track plus thumbnail buttons.
 *
 * Markup contract (rendered by templates/product.liquid):
 *   [data-gallery-track]   horizontal scroll-snap track; each direct child is one slide.
 *   [data-gallery-thumb]   buttons with data-index="<slide index>".
 *
 * Thumbnails scroll the track to their slide; the slide in view marks its thumbnail
 * with aria-current="true". Swiping the track on touch devices keeps working natively.
 */
class SThumbGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.track || this.thumbs.length === 0) return;

    this.onThumbClick = (event) => {
      const thumb = event.currentTarget;
      const index = Number(thumb.dataset.index || 0);
      const slide = this.track.children[index];
      if (!slide) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setCurrent(index);
    };
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.prototype.indexOf.call(this.track.children, entry.target);
            this.setCurrent(index);
          }
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    Array.from(this.track.children).forEach((slide) => this.observer.observe(slide));

    // Product videos: muted, looping, no controls. Play while their slide is current; click to pause/play.
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.videoToggles = Array.from(this.querySelectorAll('[data-gallery-video-toggle]'));
    this.onVideoToggle = (event) => {
      const toggle = event.currentTarget;
      const video = toggle.querySelector('video');
      if (!video) return;
      if (video.paused) {
        delete toggle.dataset.userPaused;
        this.playVideo(video);
      } else {
        toggle.dataset.userPaused = 'true';
        video.pause();
      }
      this.syncVideoLabel(toggle);
    };
    this.videoToggles.forEach((toggle) => {
      const video = toggle.querySelector('video');
      if (video) {
        video.muted = true;
        video.addEventListener('play', () => this.syncVideoLabel(toggle));
        video.addEventListener('pause', () => this.syncVideoLabel(toggle));
      }
      toggle.addEventListener('click', this.onVideoToggle);
    });

    this.setCurrent(0);
  }

  playVideo(video) {
    video.muted = true;
    const attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  }

  syncVideoLabel(toggle) {
    const video = toggle.querySelector('video');
    if (!video) return;
    const label = video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
    if (label) toggle.setAttribute('aria-label', label);
  }

  syncVideos(index) {
    if (!this.track || !this.videoToggles) return;
    Array.from(this.track.children).forEach((slide, slideIndex) => {
      const toggle = slide.querySelector('[data-gallery-video-toggle]');
      const video = toggle && toggle.querySelector('video');
      if (!video) return;
      if (slideIndex === index) {
        // Respect reduced-motion and a shopper who paused it: those start/stay paused until clicked.
        if (!this.reduceMotion && toggle.dataset.userPaused !== 'true') this.playVideo(video);
      } else {
        video.pause();
      }
    });
  }

  disconnectedCallback() {
    if (this.thumbs) this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
    if (this.observer) this.observer.disconnect();
    if (this.videoToggles) this.videoToggles.forEach((toggle) => toggle.removeEventListener('click', this.onVideoToggle));
  }

  setCurrent(index) {
    this.syncVideos(index);
    this.thumbs.forEach((thumb) => {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-thumb-gallery')) {
  customElements.define('s-thumb-gallery', SThumbGallery);
}
